import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import ServiceHeader from './components/ServiceHeader';
import TransactionHistoryModal from './components/TransactionHistoryModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import PaymentValidationModal from './components/PaymentValidationModal';
import { PaymentFlow } from './services/paymentFlow';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { toast } from "sonner";
import { Loader2, FileText, Download, CheckCircle, AlertCircle, Upload, X, GripVertical } from "lucide-react";
import { convertToUserTimezone } from './utils/timezoneHandler';
import { useTimezone } from './contexts/TimezoneContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PMPBPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatTimestamp } = useTimezone();
  
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Form state
  const [formData, setFormData] = useState({
    project_title: "",
    author_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : ""
  });
  
  // File management
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  
  // UI state
  const [showServiceCharge, setShowServiceCharge] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [serviceInfo, setServiceInfo] = useState({
    name: "PDF Merge & Print Builder",
    description: "Professional POD-ready PDF from multiple manuscripts",
    amount: 3.00  // Standard service
  });
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({
    message: '',
    progress: 0,
    step: '',
    currentFile: ''
  });
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Uploading PDFs', completed: false, current: false },
    { text: 'Merging documents', completed: false, current: false },
    { text: 'Generating final PDF', completed: false, current: false }
  ]);
  
  // Payment validation state
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  
  // Results
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [results, setResults] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  
  // Trim size options
  const trimSizes = [
    { value: "6x9", label: "6×9\" (Standard Fiction/Nonfiction)" },
    { value: "5.5x8.5", label: "5.5×8.5\" (Digest Size)" },
    { value: "5x8", label: "5×8\" (Mass Market)" },
    { value: "8x10", label: "8×10\" (Large Format)" },
    { value: "A5", label: "A5 (5.83×8.27\" - International)" }
  ];
  
  // Font options
  const fonts = [
    { value: "Times New Roman", label: "Times New Roman (Classic Serif)" },
    { value: "Garamond", label: "Garamond (Elegant Serif)" },
    { value: "Palatino", label: "Palatino (Modern Serif)" },
    { value: "Georgia", label: "Georgia (Digital-Friendly Serif)" },
    { value: "Book Antiqua", label: "Book Antiqua (Traditional Book Font)" }
  ];
  
  // On mount: load results, transactions, validate payment access
  useEffect(() => {
    loadResults();
    loadTransactions();
    
    // Initialize author name from user profile
    if (user?.first_name && user?.last_name) {
      setFormData(prev => ({
        ...prev,
        author_name: `${user.first_name} ${user.last_name}`
      }));
    }
    
    // Check if we need to load a specific result from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const resultId = urlParams.get('result');
    if (resultId) {
      loadSpecificResult(resultId);
      setPaymentCompleted(true); // Skip payment validation for viewing results
    } else {
      // NORMAL SERVICE ACCESS - VALIDATE PAYMENT FIRST
      validateServiceAccess();
    }
  }, [user]);
  
  const validateServiceAccess = async () => {
    try {
      console.log('🔍 PMPB: Starting service access validation...');
      
      const verification = await PaymentFlow.verifyPaymentForService('pmpb');
      console.log('✅ PMPB: Backend verification response:', verification);
      
      if (verification.access_granted) {
        console.log('✅ PMPB: Access granted! Setting paymentCompleted to true');
        
        // Store discount info if provided (for subscribers)
        if (verification.discount_info) {
          setChargeInfo(verification.discount_info);
        }
        
        setPaymentCompleted(true);
        console.log('✅ PMPB: Payment validation complete - user can access service');
        return;
      }
      
      if (verification.requires_payment_validation) {
        console.log('❌ PMPB: Payment validation required - showing modal');
        setShowPaymentValidation(true);
        setServiceAccessDenied(true);
        return;
      }
      
      // Fallback - deny access
      console.log('❌ PMPB: Fallback - denying access');
      setServiceAccessDenied(true);
      
    } catch (error) {
      console.error('🔥 PMPB: Service validation error:', error);
      setServiceAccessDenied(true);
    }
  };

  const handlePaymentValidationSuccess = (result, redirectLocation) => {
    const roleName = result.user_role ? result.user_role.replace('_', ' ') : 'A la Carte';
    toast.success(`Payment method added! You are now an ${roleName} user.`);
    setShowPaymentValidation(false);
    setServiceAccessDenied(false);
    setPaymentCompleted(true);
  };
  
  const loadResults = async () => {
    try {
      setIsLoadingResults(true);
      const response = await axios.get(`${API}/pmpb/results`, {
        withCredentials: true
      });
      setResults(response.data || []);
    } catch (error) {
      console.error("Error loading results:", error);
    } finally {
      setIsLoadingResults(false);
    }
  };
  
  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API}/payments/transactions`, {
        withCredentials: true
      });
      const pmpbTransactions = (response.data || []).filter(t => 
        t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())
      );
      setTransactions(pmpbTransactions);
    } catch (error) {
      console.error("Error loading transactions:", error);
    }
  };

  const loadSpecificResult = async (resultId) => {
    try {
      const response = await axios.get(`${API}/pmpb/results/${resultId}`, {
        withCredentials: true
      });
      if (response.data) {
        setResult(response.data);
        setShowResult(true);
        toast.success("Result loaded successfully");
        
        // Refresh dashboard stats after successful service completion
        window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      }
    } catch (error) {
      console.error("Error loading specific result:", error);
      toast.error("Failed to load result");
    }
  };
  
  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };
  
  const addFiles = (files) => {
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return ext.endsWith('.pdf');
    });
    
    if (validFiles.length < files.length) {
      toast.error('Only PDF files are supported');
    }
    
    if (validFiles.length > 0) {
      const newFiles = validFiles.map((file, index) => ({
        id: Date.now() + index,
        file: file,
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB"
      }));
      
      setUploadedFiles(prev => [...prev, ...newFiles]);
      toast.success(`${validFiles.length} file(s) added`);
    }
  };
  
  const removeFile = (id) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };
  
  // Drag and drop for file ordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFiles = [...uploadedFiles];
    const draggedItem = newFiles[draggedIndex];
    newFiles.splice(draggedIndex, 1);
    newFiles.splice(index, 0, draggedItem);
    
    setUploadedFiles(newFiles);
    setDraggedIndex(index);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  // Drop zone handlers
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };
  
  // Handle generate button click
  const handleGenerateClick = async () => {
    // Check payment validation first
    if (!paymentCompleted) {
      toast.error("Payment validation required. Please verify your payment method.");
      setShowPaymentValidation(true);
      return;
    }
    
    if (!formData.project_title.trim()) {
      toast.error("Project title is required");
      return;
    }
    if (!formData.author_name.trim()) {
      toast.error("Author name is required");
      return;
    }
    if (uploadedFiles.length < 2) {
      toast.error("Please upload at least 2 manuscript files");
      return;
    }
    if (uploadedFiles.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }
    
    // Refresh user profile to get latest subscription status
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const freshProfile = await response.json();
        
        // Check if user is subscriber or exempt (PREMIUM SERVICE - 50% discount for subscribers)
        const isExempt = freshProfile.role === "admin" || freshProfile.role === "beta_tester";
        const isSubscriber = freshProfile.role === "subscriber" || 
                           freshProfile.subscription_status === "active" ||
                           freshProfile.subscription_status === "monthly" || 
                           freshProfile.subscription_status === "annual";
        
        // Update serviceInfo with correct pricing
        setServiceInfo(prev => ({
          ...prev,
          amount: isExempt ? 0.00 : 3.00  // Standard service - FREE for subscribers
        }));
        
        if (isExempt || isSubscriber) {
          // Exempt users and Subscribers: Go directly to generation
          await handleGenerate();
        } else {
          // A la carte users: Show service charge modal first
          setShowServiceCharge(true);
        }
      }
    } catch (error) {
      console.error("Error refreshing profile:", error);
      toast.error("Failed to verify account status");
    }
  };
  
  // Handle service charge confirmation
  const handleServiceChargeConfirm = async () => {
    setShowServiceCharge(false);
    await handleGenerate();
  };
  
  // Main processing function
  const handleGenerate = async () => {
    setShowProcessingModal(true);
    
    try {
      // Step 1: Uploading
      setCurrentStep('Uploading PDF files to server...');
      setProgressSteps([
        { text: 'Uploading PDFs', completed: false, current: true },
        { text: 'Merging documents', completed: false, current: false },
        { text: 'Generating final PDF', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      // Create FormData for multi-file upload
      const formDataToSend = new FormData();
      formDataToSend.append('project_title', formData.project_title);
      formDataToSend.append('author_name', formData.author_name);
      formDataToSend.append('file_order', JSON.stringify(uploadedFiles.map(f => f.name)));
      
      uploadedFiles.forEach((fileObj) => {
        formDataToSend.append('files', fileObj.file);
      });
      
      setProcessingProgress(25);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Merging
      setCurrentStep('Merging documents in specified order...');
      setProgressSteps([
        { text: 'Uploading PDFs', completed: true, current: false },
        { text: 'Merging documents', completed: false, current: true },
        { text: 'Generating final PDF', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      // Make API call
      const response = await axios.post(
        `${API}/pmpb/merge-and-generate`,
        formDataToSend,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setProcessingProgress(70);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 3: Generating final PDF
      setCurrentStep('Generating print-ready PDF...');
      setProgressSteps([
        { text: 'Uploading PDFs', completed: true, current: false },
        { text: 'Merging documents', completed: true, current: false },
        { text: 'Generating final PDF', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProcessingStatus({
        phase: "Complete",
        message: "PDF generation complete!",
        progress: 100
      });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Complete
      setProgressSteps([
        { text: 'Uploading PDFs', completed: true, current: false },
        { text: 'Merging documents', completed: true, current: false },
        { text: 'Generating final PDF', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Set result and switch to result view
      setResult(response.data);
      setShowResult(true);
      
      // Store transaction ID for email receipt
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
      }
      
      // Close processing modal
      setShowProcessingModal(false);
      
      // Show success
      toast.success("Print-ready PDF generated successfully!");
      
      // Show email receipt modal if charged
      if (response.data.charged || response.data.transaction_id) {
        setTimeout(() => {
          setShowEmailReceiptModal(true);
        }, 500);
      }
      
      // Reload results and transactions
      loadResults();
      loadTransactions();
      
    } catch (error) {
      console.error("PMPB Error:", error);
      
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail || "Insufficient balance");
      } else {
        toast.error(error.response?.data?.detail || "PDF generation failed");
      }
    } finally {
      setShowProcessingModal(false);
    }
  };
  
  // Download PDF
  const handleDownload = async (resultId, projectTitle) => {
    try {
      const response = await axios.get(`${API}/pmpb/download/${resultId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${projectTitle}-PRINT-READY.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    }
  };
  
  // Delete result
  const handleDelete = async (resultId) => {
    if (!window.confirm("Are you sure you want to delete this result?")) return;
    
    try {
      await axios.delete(`${API}/pmpb/results/${resultId}`, {
        withCredentials: true
      });
      
      toast.success("Result deleted");
      loadResults();
      if (currentResult?.id === resultId) {
        setCurrentResult(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete result");
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-white">
      {!showResult ? (
        <>
          {/* Header with Logo and Navigation */}
          <header className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img 
                    src="/Digital_Ink_AI_Logo.png" 
                    alt="Digital Ink AI Logo" 
                    className="h-12 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/homepage')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    🏠 Home
                  </button>
                  <button
                    onClick={() => navigate('/user_dashboard')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    📊 Dashboard
                  </button>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📄 Print-Ready Merged PDF Builder
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Transform multiple manuscript files into a single, professionally formatted print-ready PDF optimized for Print-On-Demand platforms. Upload your chapters or document sections, arrange them in order, and receive a publication-ready PDF with consistent formatting, proper pagination, and embedded metadata meeting industry standards for POD printing services.
              </p>
            </div>

            {/* Pricing Info */}
            {user && (
              <div className="mb-8 bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200 rounded-lg p-6">
                <div className="text-center">
                  {user.subscription_status !== 'None' || user.role === 'admin' || user.role === 'beta_tester' ? (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - merge as many PDFs as you need!</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-rose-900 mb-2">💎 Standard Service - $3.00</h3>
                      <p className="text-rose-700">Professional PDF merging charged to your payment method on file</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form Page */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Main Form */}
              <div className="lg:col-span-2 space-y-6">

            {/* Main Form Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-rose-900">Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Project Details */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="project_title" className="text-sm font-medium">
                      Project Title *
                    </Label>
                    <Input
                      id="project_title"
                      value={formData.project_title}
                      onChange={(e) => setFormData({...formData, project_title: e.target.value})}
                      placeholder="Enter your book or project title"
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="author_name" className="text-sm font-medium">
                      Author Name *
                    </Label>
                    <Input
                      id="author_name"
                      value={formData.author_name}
                      onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                      placeholder="Your name as author"
                      className="mt-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* File Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-rose-900">Upload Manuscript Files</CardTitle>
                <p className="text-sm text-slate-600 mt-1">
                  Upload 2-10 PDF files to merge into your print-ready PDF
                </p>
              </CardHeader>
              <CardContent>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging ? 'border-pink-500 bg-pink-50' : 'border-slate-300 hover:border-pink-400'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <div className="flex flex-col items-center">
                    <div className="bg-pink-100 p-4 rounded-full mb-4">
                      <Upload className="h-8 w-8 text-pink-600" />
                    </div>
                    <p className="text-base font-medium text-slate-700 mb-2">
                      Drop your manuscript files here
                    </p>
                    <p className="text-sm text-slate-500 mb-4">
                      or click to browse from your computer
                    </p>
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Select Files
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="mt-4 text-xs text-slate-500 space-y-1">
                      <p>✓ Supported: PDF files only</p>
                      <p>✓ Maximum: 10MB per file</p>
                      <p>✓ Required: 2-10 files total</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-rose-900">
                    Document Order ({uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''})
                  </CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    Drag files to reorder them in your final PDF
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {uploadedFiles.map((fileObj, index) => (
                      <div
                        key={fileObj.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 p-4 bg-slate-50 border-2 rounded-lg cursor-move hover:border-pink-300 hover:bg-pink-50 transition-all ${
                          draggedIndex === index ? 'opacity-50 scale-95' : ''
                        }`}
                      >
                        <GripVertical className="h-5 w-5 text-slate-400" />
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{fileObj.name}</p>
                          <p className="text-xs text-slate-500">{fileObj.size}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(fileObj.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Generate Button */}
            <div className="flex justify-center">
              <button
                onClick={handleGenerateClick}
                disabled={!formData.project_title || !formData.author_name || uploadedFiles.length < 2 || !paymentCompleted}
                className="w-full bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Print-Ready PDF with Digital Ink AI
              </button>
            </div>
            
            {/* Payment Validation Message */}
            {serviceAccessDenied && (
              <div className="text-center mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800">
                  ⚠️ Payment method required to access this service. Please add a payment method to continue.
                </p>
                <Button
                  onClick={validateServiceAccess}
                  className="mt-2 bg-yellow-600 hover:bg-yellow-700"
                  size="sm"
                >
                  Verify Payment Method
                </Button>
              </div>
            )}
          </div>

          {/* Right Column - Information Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Process Information */}
            <Card className="border-2 border-pink-200">
              <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50">
                <CardTitle className="text-lg text-rose-900">📋 How It Works</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Upload Files</p>
                      <p className="text-xs text-slate-600">Select 2-10 PDF manuscript files</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Arrange Order</p>
                      <p className="text-xs text-slate-600">Drag files to set the correct sequence</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Configure Layout</p>
                      <p className="text-xs text-slate-600">Choose trim size and font family</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center font-bold text-sm">
                      4
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Generate PDF</p>
                      <p className="text-xs text-slate-600">Receive print-ready PDF in minutes</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Generation Features */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Professional print layout with industry-standard margins</span>
                </div>
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Merged documents in specified order</span>
                </div>
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Consistent typography throughout</span>
                </div>
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Ready for Amazon KDP, IngramSpark, Lulu</span>
                </div>
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Instant download upon completion</span>
                </div>
                <div className="flex items-start">
                  <span className="text-pink-500 mr-2">✓</span>
                  <span>Supports 2-10 PDF files (10MB each max)</span>
                </div>
              </div>
            </div>

            {/* Compatible Platforms */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🖨️ Compatible Platforms</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">•</span>
                  <span><strong>Amazon KDP Print:</strong> Ready for upload</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">•</span>
                  <span><strong>IngramSpark:</strong> Industry-standard format</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">•</span>
                  <span><strong>Lulu:</strong> Print-ready specifications</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">•</span>
                  <span><strong>All POD Services:</strong> Universal compatibility</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </main>
        </>
        ) : (
          /* Result Display Page */
          <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-slate-100">
            {/* Sticky Header with Logo and Navigation - Matching BVADM */}
            <header className="bg-white shadow-sm border-b print:hidden sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
            <Card className="border-2 border-pink-200">
              <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl text-rose-900 mb-2">
                      ✅ PDF Merge Complete!
                    </CardTitle>
                    <p className="text-sm text-slate-600">
                      Your print-ready PDF has been generated and is ready for download
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setShowResult(false);
                      setResult(null);
                      // Reset form
                      setFormData({
                        project_title: "",
                        author_name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : ""
                      });
                      setUploadedFiles([]);
                    }}
                    variant="outline"
                    className="border-pink-300 text-pink-700 hover:bg-pink-50"
                  >
                    Merge Another PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Project Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <h3 className="font-semibold text-lg text-slate-900">{result?.project_title}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Author:</span>
                      <span className="ml-2 font-medium">{result?.author_name}</span>
                    </div>
                    {/* Trim size and font family removed */}
                    <div>
                      <span className="text-slate-600">Total Pages:</span>
                      <span className="ml-2 font-medium">~{result?.total_page_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Files Merged:</span>
                      <span className="ml-2 font-medium">{result?.source_file_count}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Created:</span>
                      <span className="ml-2 font-medium">{formatTimestamp(result?.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Validation Notes */}
                {result?.validation_notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-amber-900 mb-1">Processing Notes</h4>
                        <p className="text-sm text-amber-800">{result.validation_notes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={() => handleDownload(result?.id, result?.project_title)}
                    className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white px-8 py-6 text-lg font-semibold"
                    size="lg"
                  >
                    <Download className="mr-2 h-6 w-6" />
                    Download Print-Ready PDF
                  </Button>
                </div>

                {/* POD Platform Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">📖 Ready for Print-on-Demand</h4>
                  <p className="text-sm text-blue-800 mb-2">
                    Your PDF is formatted and ready to upload to:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center text-blue-700">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Amazon KDP Print
                    </div>
                    <div className="flex items-center text-blue-700">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      IngramSpark
                    </div>
                    <div className="flex items-center text-blue-700">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Lulu
                    </div>
                    <div className="flex items-center text-blue-700">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Other POD Services
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
              </div>
            </main>
          </div>
        )
      }
      
      {/* Service Charge Modal */}
      <ServiceChargeModal
        isOpen={showServiceCharge}
        onClose={() => setShowServiceCharge(false)}
        onConfirm={handleServiceChargeConfirm}
        serviceInfo={serviceInfo}
        userType={user?.role}
        user={user}
      />
      
      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={() => setShowEmailReceiptModal(false)}
        onSendReceipt={async () => {
          try {
            await axios.post(`${API}/send-receipt-email`, {
              transaction_id: lastTransactionId,
              user_email: user.email
            }, { withCredentials: true });
            toast.success("Receipt email sent successfully!");
            setShowEmailReceiptModal(false);
          } catch (error) {
            console.error('Receipt email error:', error);
            toast.error("Failed to send receipt email");
            setShowEmailReceiptModal(false);
          }
        }}
        serviceName="Print-Ready Merged PDF Builder (PMPB)"
        amount={serviceInfo.amount}
        bookTitle={result?.project_title}
      />
      
      {/* Processing Modal */}
      <ProcessingModal
        isOpen={showProcessingModal}
        serviceName="PDF Merge & Print Builder (PMPB)"
        bookTitle={formData.project_title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />
      
      {/* Transaction History Modal */}
      <TransactionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        service="PMPB"
      />
      
      {/* Payment Validation Modal */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={() => {
          setShowPaymentValidation(false);
          navigate('/homepage');
        }}
        onSuccess={handlePaymentValidationSuccess}
        serviceName="Print-Ready Merged PDF Builder (PMPB)"
      />
    </div>
  );
};

export default PMPBPage;
